/** The lifecycle spine (plan §1.1): five connected nodes — Booked, Pre-check-in,
 *  Departure, On-site, After trip — sticky under the page tabs. Past nodes solid, the
 *  current one navy-filled and ringed, future ones outlined; a "now" tick on the track
 *  after the current node. The order record renders its four (Placed · Contact ·
 *  Bookings · Money) through the same component (`label` names the navigation).
 *
 *  The nodes are the section index: a click (or `1`–`5` outside inputs, bound by the
 *  page) scrolls to the stage panel and focuses it. In edit mode a node carries its
 *  stage's unsaved-change count and, amber, how many of them the restech's next update
 *  will undo — each with an icon or number AND words for assistive tech. On narrow
 *  content (`compactMarks`, below 1100 px) the marks shrink to a dot and the sync icon,
 *  their counts in the tooltip, so a stage's name is never cut; its datum lines wrap.
 *
 *  A `<nav>` of an ordered list of buttons; the current stage is `aria-current="step"`. */
import SyncIcon from '@mui/icons-material/Sync';
import { alpha, Box, ButtonBase, Tooltip, Typography, type Theme } from '@mui/material';
import { forwardRef, type ForwardedRef, type ReactElement, type Ref } from 'react';

import { SCROLLER_PADDING_PX } from '@/features/records/page/recordPage';

import type { SpineStage } from '../registry';

import type { SpineModel, SpineNodeState } from './spineModel';

export interface StageMarks {
  /** Unsaved changes in the stage. */
  count: number;
  /** How many of them the restech's next update will undo. */
  reverts: number;
}

export interface SpineNavProps<S extends string = SpineStage> {
  model: SpineModel<S>;
  /** Edit mode: show the marks. */
  editing: boolean;
  marks: Partial<Record<S, StageMarks>>;
  /** Display name of the restech ("Ventrata") for the revert mark's words. */
  managedBy: string | null;
  onSelect: (stage: S) => void;
  /** The navigation's accessible name (default "Booking lifecycle"). */
  label?: string;
  /** Narrow content: the marks shrink to a dot and the sync icon (`MarkBadges`). */
  compactMarks?: boolean;
}

const NODE = 14;
/** A node's datum lines wrap at spaces; a token wider than the node (an email) is cut. */
const DATUM_SX = { display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', fontVariantNumeric: 'tabular-nums' } as const;

function nodeSx(state: SpineNodeState) {
  return (t: Theme) => {
    const navy = t.palette.primary.main;
    const base = {
      width: NODE,
      height: NODE,
      borderRadius: '50%',
      flex: 'none',
      display: 'grid',
      placeItems: 'center',
      boxSizing: 'border-box' as const,
      position: 'relative' as const,
      zIndex: 1,
    };
    if (state === 'past') {
      return {
        ...base,
        bgcolor: navy,
        '&::after': { content: '""', width: 4, height: 4, borderRadius: '50%', bgcolor: t.palette.background.paper },
      };
    }
    if (state === 'current') {
      return { ...base, bgcolor: navy, outline: `2px solid ${alpha(navy, 0.3)}`, outlineOffset: 2 };
    }
    return { ...base, bgcolor: t.palette.background.paper, border: `2px solid ${alpha(navy, 0.35)}` };
  };
}

function segmentSx(solid: boolean, flex: string | number = 1) {
  return (t: Theme) => ({
    flex,
    height: 0,
    mx: 0.5,
    borderTop: solid ? `2px solid ${t.palette.primary.main}` : `2px dashed ${alpha(t.palette.primary.main, 0.35)}`,
  });
}

/** A section's marks in words, for its accessible name ('' when clean). */
export function marksLabel(m: StageMarks | undefined, managedBy: string | null): string {
  if (!m || m.count === 0) return '';
  const changes = `${m.count} unsaved ${m.count === 1 ? 'change' : 'changes'}`;
  const reverts = m.reverts > 0 ? `, ${m.reverts} of them undone by ${managedBy ?? 'the feed'}'s next update` : '';
  return `${changes}${reverts}`;
}

function SpineNavInner<S extends string>(
  { model, editing, marks, managedBy, onSelect, label = 'Booking lifecycle', compactMarks = false }: SpineNavProps<S>,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const last = model.nodes.length - 1;
  return (
    <Box
      ref={ref}
      data-spine
      sx={{
        position: 'sticky',
        top: -SCROLLER_PADDING_PX,
        zIndex: 3,
        bgcolor: 'background.default',
        mx: -3,
        px: 3,
        pt: 1.5,
        pb: 1.25,
        mb: 2,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Box component="nav" aria-label={label}>
        <Box
          component="ol"
          sx={{
            listStyle: 'none',
            // Room for the focus ring, which the sideways scroll would clip.
            m: -0.75,
            p: 0.75,
            display: 'grid',
            gridTemplateColumns: `repeat(${model.nodes.length}, minmax(132px, 1fr))`,
            overflowX: 'auto',
          }}
        >
          {model.nodes.map((n, i) => {
            const m = editing ? marks[n.stage] : undefined;
            const isCurrent = i === model.current;
            const extra = marksLabel(m, managedBy);
            return (
              <Box component="li" key={n.stage} sx={{ minWidth: 0 }}>
                <ButtonBase
                  onClick={() => onSelect(n.stage)}
                  aria-current={isCurrent ? 'step' : undefined}
                  aria-label={[`${i + 1}. ${n.title}: ${n.datum}`, n.detail, isCurrent ? 'current stage' : null, extra || null]
                    .filter(Boolean)
                    .join('. ')}
                  data-stage={n.stage}
                  data-state={n.state}
                  sx={{
                    width: '100%',
                    display: 'block',
                    textAlign: 'left',
                    pr: 1.5,
                    borderRadius: 1,
                    '&:hover .spine-title': { color: 'primary.main', textDecoration: 'underline', textUnderlineOffset: 3 },
                    // Around the node (and the current node's ring) and its text, clear of
                    // the next node; the list's padding keeps it unclipped.
                    '&.Mui-focusVisible::after': {
                      content: '""',
                      position: 'absolute',
                      inset: '-6px 6px -4px -6px',
                      border: 2,
                      borderColor: 'primary.main',
                      borderRadius: 1,
                      pointerEvents: 'none',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', height: 22 }} aria-hidden>
                    <Box sx={nodeSx(n.state)} />
                    {i < last ? (
                      isCurrent ? (
                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', minWidth: 0 }}>
                          <Box sx={segmentSx(false, '0 0 18%')} />
                          <NowTick />
                          <Box sx={segmentSx(false)} />
                        </Box>
                      ) : (
                        <Box sx={segmentSx(i < model.current)} />
                      )
                    ) : isCurrent ? (
                      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', pl: 1 }}>
                        <NowTick />
                      </Box>
                    ) : (
                      <Box sx={{ flex: 1 }} />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, minWidth: 0 }}>
                    <Typography className="spine-title" variant="body2" component="span" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {n.title}
                    </Typography>
                    <MarkBadges marks={m} managedBy={managedBy} compact={compactMarks} />
                  </Box>
                  <Typography variant="caption" component="span" sx={{ ...DATUM_SX, color: 'text.primary' }}>
                    {n.datum}
                  </Typography>
                  <Typography variant="caption" component="span" color="text.secondary" sx={{ ...DATUM_SX, minHeight: 20 }}>
                    {n.detail ?? ''}
                  </Typography>
                </ButtonBase>
              </Box>
            );
          })}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', mt: 0.25 }}>
          Times in {model.timezone}
        </Typography>
      </Box>
    </Box>
  );
}

/** Generic over the record kind's stages (forwardRef erases type parameters). */
export const SpineNav = forwardRef(SpineNavInner) as <S extends string = SpineStage>(
  props: SpineNavProps<S> & { ref?: Ref<HTMLDivElement> },
) => ReactElement;

/** A section's edit-mode marks — its unsaved-change count (navy) and, amber with the
 *  sync icon, how many of them the restech's next update will undo. Decorative: the
 *  control that carries them names them (`marksLabel`). Shared by the spine nodes and
 *  the tabs view's tabs. `compact`: a navy dot and the amber sync icon, the counts in
 *  their tooltips. */
export function MarkBadges({
  marks: m,
  managedBy,
  compact = false,
}: {
  marks: StageMarks | undefined;
  managedBy: string | null;
  compact?: boolean;
}) {
  if (!m || m.count === 0) return null;
  const revertsText = `${m.reverts} will be undone by ${managedBy ?? 'the feed'}'s next update`;
  if (compact) {
    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flex: 'none' }}>
        <Tooltip title={`${m.count} unsaved ${m.count === 1 ? 'change' : 'changes'}`}>
          <Box
            component="span"
            aria-hidden
            data-dirty={m.count}
            sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', flex: 'none' }}
          />
        </Tooltip>
        {m.reverts > 0 && (
          <Tooltip title={revertsText}>
            <Box
              component="span"
              aria-hidden
              data-reverts={m.reverts}
              sx={(t) => ({
                width: 18,
                height: 18,
                borderRadius: '50%',
                border: `1px solid ${alpha(t.palette.warning.main, 0.5)}`,
                bgcolor: alpha(t.palette.warning.main, 0.12),
                color: t.palette.warning.dark,
                display: 'inline-grid',
                placeItems: 'center',
                flex: 'none',
              })}
            >
              <SyncIcon sx={{ fontSize: 12 }} />
            </Box>
          </Tooltip>
        )}
      </Box>
    );
  }
  return (
    <>
      <Box
        component="span"
        aria-hidden
        data-dirty={m.count}
        sx={{
          minWidth: 18,
          height: 18,
          px: 0.5,
          borderRadius: 9,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          fontSize: 11,
          fontWeight: 700,
          display: 'inline-grid',
          placeItems: 'center',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {m.count}
      </Box>
      {m.reverts > 0 && (
        <Tooltip title={revertsText}>
          <Box
            component="span"
            aria-hidden
            data-reverts={m.reverts}
            sx={(t) => ({
              height: 18,
              px: 0.5,
              borderRadius: 9,
              border: `1px solid ${alpha(t.palette.warning.main, 0.5)}`,
              bgcolor: alpha(t.palette.warning.main, 0.12),
              color: t.palette.warning.dark,
              fontSize: 11,
              fontWeight: 700,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.25,
              fontVariantNumeric: 'tabular-nums',
            })}
          >
            <SyncIcon sx={{ fontSize: 12 }} />
            {m.reverts}
          </Box>
        </Tooltip>
      )}
    </>
  );
}

function NowTick() {
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flex: 'none', px: 0.25 }}>
      <Box sx={{ width: 2, height: 12, bgcolor: 'primary.main', borderRadius: 1 }} />
      <Typography component="span" sx={{ fontSize: 10.5, fontWeight: 700, color: 'primary.main', lineHeight: 1 }}>
        now
      </Typography>
    </Box>
  );
}
